import { CssBaseline, ThemeProvider } from "@mui/material"
import { extendTheme } from "@mui/material/styles"
import { PropsWithChildren } from "react"
import breakpoints from "./breakpoints"
import {
  ERROR_DARK,
  ERROR_LIGHT,
  GREY_DARK,
  GREY_LIGHT,
  INFO_DARK,
  INFO_LIGHT,
  mapColorsToMuiToken,
  PRIMARY_DARK,
  PRIMARY_LIGHT,
  SECONDARY_DARK,
  SECONDARY_LIGHT,
  SUCCESS_DARK,
  SUCCESS_LIGHT,
  WARNING_DARK,
  WARNING_LIGHT
} from "./colorPalette"
import typography from "./typography"

const theme = extendTheme({
  colorSchemeSelector: "class",
  typography,
  breakpoints,
  shape: { borderRadius: 8 },
  components: {
    MuiStack: {
      defaultProps: {
        useFlexGap: true
      }
    },
    MuiTextField: {
      defaultProps: {
        size: "small", // or 'medium'
        variant: "outlined"
      }
    },
    MuiInputBase: {
      defaultProps: {
        size: "small"
      },
      styleOverrides: {
        root: () => ({
          "& .MuiOutlinedInput-notchedOutline": {
            // borderColor: theme.palette.grey[400],
          },
          "&&:hover .MuiOutlinedInput-notchedOutline": {
            // borderColor: theme.vars.palette.grey[500],
          },
          "&&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.vars.palette.text.primary // stays gray when focused
            // borderWidth: 2, // optional visual emphasis
          }
        })
      }
    },
    MuiSelect: {
      defaultProps: {
        size: "small"
      }
    },
    MuiAutocomplete: {
      defaultProps: {
        size: "small"
      }
    },
    MuiFormControl: {
      defaultProps: {
        size: "small",
        margin: "dense" // optional: reduces vertical space
      }
    },
    MuiOutlinedInput: {
      defaultProps: {
        size: "small"
      },
      styleOverrides: {
        root: {
          "& legend": { display: "none" },
          "& fieldset": { top: 0 }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          position: "static",
          transform: "none",
          transition: "none",
          marginBottom: theme.spacing(0.5),
          fontSize: theme.typography.body2.fontSize,
          color: theme.vars ? theme.vars.palette.text.primary : theme.palette.text.primary,
          lineHeight: 1.4, // keeps it aligned naturally
          textAlign: "left",
          // Remove vertical centering
          top: "unset",
          left: "unset",
          transformOrigin: "unset",
          "&.Mui-focused": {
            color: theme.vars ? theme.vars.palette.text.secondary : theme.palette.text.secondary
          },
          "&.MuiInputLabel-shrink": {
            transform: "none"
          }
        })
      }
    },
    // @ts-ignore — MuiDatePicker theme key is provided by @mui/x-date-pickers but missing from extendTheme Components
    MuiDatePicker: {
      defaultProps: {
        margin: "dense",
        size: "small"
      },
      styleOverrides: {
        // @ts-ignore
        root: ({ theme }) => ({
          "& legend": { display: "none" },
          "& fieldset": { top: 0 },
          "&&.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: theme.vars.palette.text.primary + "!important" // stays gray when focused
            // borderWidth: 2, // optional visual emphasis
          }
        })
      }
    }
  },
  colorSchemes: {
    light: {
      palette: {
        primary: {
          ...mapColorsToMuiToken(PRIMARY_LIGHT),
          contrastText: "#FFFFFF"
        },
        secondary: {
          ...mapColorsToMuiToken(SECONDARY_LIGHT),
          contrastText: "#FFFFFF"
        },
        grey: {
          ...mapColorsToMuiToken(GREY_LIGHT)
        },
        error: {
          ...mapColorsToMuiToken(ERROR_LIGHT),
          contrastText: "#FFFFFF"
        },
        warning: {
          ...mapColorsToMuiToken(WARNING_LIGHT),
          contrastText: "#fff"
        },
        info: {
          ...mapColorsToMuiToken(INFO_LIGHT),
          contrastText: "#fff"
        },
        success: {
          ...mapColorsToMuiToken(SUCCESS_LIGHT),
          contrastText: "#fff"
        }
      }
    },
    dark: {
      palette: {
        primary: {
          ...mapColorsToMuiToken(PRIMARY_DARK),
          contrastText: "#FFFFFF"
        },
        secondary: {
          ...mapColorsToMuiToken(SECONDARY_DARK),
          contrastText: "#FFFFFF"
        },
        grey: {
          ...mapColorsToMuiToken(GREY_DARK)
        },
        error: {
          ...mapColorsToMuiToken(ERROR_DARK),
          contrastText: "#FFFFFF"
        },
        warning: {
          ...mapColorsToMuiToken(WARNING_DARK),
          contrastText: "#fff"
        },
        info: {
          ...mapColorsToMuiToken(INFO_DARK),
          contrastText: "#fff"
        },
        success: {
          ...mapColorsToMuiToken(SUCCESS_DARK),
          contrastText: "#fff"
        }
      }
    }
  }
})

export function AppThemeProvider({ children }: PropsWithChildren) {
  // might need to split this up because of inject first for storybook
  return (
    <ThemeProvider theme={theme} defaultMode={"light"}>
      <CssBaseline enableColorScheme />
      {children}
    </ThemeProvider>
  )
}
